import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCustomSet, CustomSetWithContent } from '../services/api';
import WordCard from '../components/WordCard';
import { speak } from '../services/audio';
import './CustomSetLearn.css';

type ViewMode = 'overview' | 'word-by-word' | 'self-eval' | 'completed';

interface WordState {
  id: string;
  excluded: boolean;
  hardLock: number;
}

const CustomSetLearn: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [set, setSet] = useState<CustomSetWithContent | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // Self-evaluation state
  const [wordStates, setWordStates] = useState<Map<string, WordState>>(new Map());
  const [selfEvalIndex, setSelfEvalIndex] = useState(0);

  // Challenge modal state
  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeInput, setChallengeInput] = useState('');
  const [challengeAttempts, setChallengeAttempts] = useState(0);
  const [challengeFeedback, setChallengeeFeedback] = useState<'none' | 'success' | 'retry' | 'fail'>('none');

  useEffect(() => {
    if (id) loadSet();
  }, [id]);

  const loadSet = async () => {
    if (!id) return;
    try {
      const data = await getCustomSet(id);
      setSet(data);
    } catch (err) {
      console.error('Failed to load set:', err);
    } finally {
      setLoading(false);
    }
  };

  const wordCount = set?.content.filter(c => c.type === 'word').length || 0;
  const sentenceCount = set?.content.filter(c => c.type === 'sentence').length || 0;

  const startWordByWord = () => {
    setCurrentIndex(0);
    setViewMode('word-by-word');
    if (set && set.content.length > 0) {
      speak(set.content[0].english);
    }
  };

  const startSelfEval = () => {
    if (!set) return;
    const states = new Map<string, WordState>();
    set.content.forEach(c => {
      states.set(c.id, { id: c.id, excluded: false, hardLock: 0 });
    });
    setWordStates(states);
    setSelfEvalIndex(0);
    setViewMode('self-eval');
    if (set.content.length > 0) {
      speak(set.content[0].english);
    }
  };

  const getActiveWords = () => {
    if (!set) return [];
    return set.content.filter(c => {
      const state = wordStates.get(c.id);
      return !state || !state.excluded;
    });
  };

  const handleEasyClick = () => {
    // 30% chance to show challenge
    if (Math.random() < 0.3) {
      setShowChallenge(true);
      setChallengeInput('');
      setChallengeAttempts(0);
      setChallengeeFeedback('none');
    } else {
      handleSelfEvalNext('easy');
    }
  };

  const handleChallengeSubmit = () => {
    if (!set) return;
    const activeWords = getActiveWords();
    const currentWord = activeWords[selfEvalIndex % activeWords.length];

    const normalize = (s: string) => s.trim().replace(/[.!?]+$/, '').toLowerCase();
    if (normalize(challengeInput) === normalize(currentWord.english)) {
      // Correct answer
      setChallengeeFeedback('success');
      setTimeout(() => {
        setShowChallenge(false);
        handleSelfEvalNext('easy');
      }, 1000);
    } else {
      // Wrong answer
      const newAttempts = challengeAttempts + 1;
      setChallengeAttempts(newAttempts);

      if (newAttempts >= 2) {
        // Failed after 2 attempts
        setChallengeeFeedback('fail');
      } else {
        // First wrong attempt
        setChallengeeFeedback('retry');
      }
    }
  };

  const handleSelfEvalNext = (action: 'easy' | 'ok' | 'hard') => {
    if (!set) return;
    const activeWords = getActiveWords();
    if (activeWords.length === 0) return;

    const currentWord = activeWords[selfEvalIndex % activeWords.length];
    const newStates = new Map(wordStates);
    const currentState = newStates.get(currentWord.id) || { id: currentWord.id, excluded: false, hardLock: 0 };

    if (action === 'easy' && currentState.hardLock === 0) {
      newStates.set(currentWord.id, { ...currentState, excluded: true });
    } else if (action === 'hard') {
      newStates.set(currentWord.id, { ...currentState, hardLock: 2 });
    } else if (action === 'ok') {
      if (currentState.hardLock > 0) {
        newStates.set(currentWord.id, { ...currentState, hardLock: currentState.hardLock - 1 });
      }
    }

    setWordStates(newStates);

    const remainingWords = set.content.filter(c => {
      const state = newStates.get(c.id);
      return !state || !state.excluded;
    });

    if (remainingWords.length === 0) {
      setViewMode('completed');
      return;
    }

    const nextIndex = (selfEvalIndex + 1) % remainingWords.length;
    setSelfEvalIndex(nextIndex);
    speak(remainingWords[nextIndex].english);
  };

  const handleNext = () => {
    if (!set) return;
    if (currentIndex < set.content.length - 1) {
      setCurrentIndex(currentIndex + 1);
      speak(set.content[currentIndex + 1].english);
    } else {
      setViewMode('completed');
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      if (set) speak(set.content[currentIndex - 1].english);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  if (!set) {
    return (
      <div className="error-page">
        <h2>Nie znaleziono zestawu</h2>
        <button className="btn btn-primary" onClick={() => navigate('/custom-sets')}>
          Wróć do zestawów
        </button>
      </div>
    );
  }

  if (set.content.length === 0) {
    return (
      <div className="custom-set-learn fade-in">
        <div className="empty-set">
          <div className="empty-icon">📭</div>
          <h3>Ten zestaw jest pusty</h3>
          <p>Dodaj słówka, aby rozpocząć naukę!</p>
          <button className="btn btn-primary" onClick={() => navigate(`/custom-sets/${id}`)}>
            Dodaj słówka
          </button>
        </div>
      </div>
    );
  }

  // Completed view
  if (viewMode === 'completed') {
    return (
      <div className="lesson-complete fade-in">
        <div className="complete-icon">🎉</div>
        <h1>Świetnie!</h1>
        <p>Ukończyłeś zestaw "{set.name}"</p>
        <p className="stats-summary">
          Przejrzałeś {set.content.length} słówek!
        </p>
        <div className="complete-actions">
          <button className="btn btn-secondary btn-large" onClick={() => setViewMode('overview')}>
            🔄 Powtórz
          </button>
          <button className="btn btn-primary btn-large" onClick={() => navigate('/custom-sets')}>
            Wróć do zestawów
          </button>
        </div>
      </div>
    );
  }

  // Self-evaluation view
  if (viewMode === 'self-eval') {
    const activeWords = getActiveWords();
    if (activeWords.length === 0) {
      setViewMode('completed');
      return null;
    }
    const currentWord = activeWords[selfEvalIndex % activeWords.length];
    const currentState = wordStates.get(currentWord.id);
    const isHardLocked = currentState && currentState.hardLock > 0;
    const excludedCount = set.content.length - activeWords.length;

    return (
      <div className="custom-set-learn fade-in">
        <div className="learn-header">
          <button className="btn btn-secondary btn-small" onClick={() => setViewMode('overview')}>
            ← Wróć
          </button>
          <h2>{set.icon} {set.name}</h2>
          <div className="progress-indicator">
            {excludedCount} / {set.content.length} ✓
          </div>
        </div>

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(excludedCount / set.content.length) * 100}%` }}
          />
        </div>

        <div className={`card-container ${showChallenge ? 'blurred' : ''}`}>
          <WordCard content={currentWord} size="large" />
        </div>

        {showChallenge && (
          <div className="challenge-modal">
            <div className="challenge-content">
              <h3>Czy oby na pewno łatwizna?</h3>
              <p>Napisz po angielsku: <strong>{currentWord.polish}</strong></p>
              <input
                type="text"
                className="challenge-input"
                value={challengeInput}
                onChange={(e) => setChallengeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleChallengeSubmit()}
                autoFocus
                disabled={challengeFeedback === 'success' || challengeFeedback === 'fail'}
              />
              {challengeFeedback === 'none' && (
                <button className="btn btn-primary" onClick={handleChallengeSubmit}>
                  Sprawdź
                </button>
              )}
              {challengeFeedback === 'success' && (
                <p className="challenge-feedback success">✅ Nieźle!</p>
              )}
              {challengeFeedback === 'retry' && (
                <>
                  <p className="challenge-feedback retry">❌ Nie do końca - spróbuj poprawić</p>
                  <button className="btn btn-primary" onClick={handleChallengeSubmit}>
                    Sprawdź ponownie
                  </button>
                </>
              )}
              {challengeFeedback === 'fail' && (
                <>
                  <p className="challenge-feedback fail">
                    😔 Niestety, trzeba jeszcze poćwiczyć! Słowo, którego szukasz to: <strong>{currentWord.english}</strong>
                  </p>
                  <button
                    className="btn btn-secondary challenge-close-btn"
                    onClick={() => {
                      setShowChallenge(false);
                      handleSelfEvalNext('ok');
                    }}
                  >
                    No dobra...
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {!showChallenge && (
          <div className="self-eval-box">
            <p className="self-eval-prompt">Czy znasz to słówko?</p>
            <div className="self-eval-buttons">
              {!isHardLocked && (
                <button
                  className="btn self-eval-btn easy"
                  onClick={handleEasyClick}
                >
                  ✅ Łatwizna!
                </button>
              )}
              <button
                className="btn self-eval-btn ok"
                onClick={() => handleSelfEvalNext('ok')}
              >
                🤔 Dobra, chyba pamiętam
              </button>
              <button
                className="btn self-eval-btn hard"
                onClick={() => handleSelfEvalNext('hard')}
              >
                😰 Oj, trudne!
              </button>
            </div>
            <p className="self-eval-remaining">Pozostało: {activeWords.length} słów</p>
          </div>
        )}
      </div>
    );
  }

  // Word-by-word view
  if (viewMode === 'word-by-word') {
    const currentContent = set.content[currentIndex];
    return (
      <div className="custom-set-learn fade-in">
        <div className="learn-header">
          <button className="btn btn-secondary btn-small" onClick={() => setViewMode('overview')}>
            ← Wróć
          </button>
          <h2>{set.icon} {set.name}</h2>
          <div className="progress-indicator">
            {currentIndex + 1} / {set.content.length}
          </div>
        </div>

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${((currentIndex + 1) / set.content.length) * 100}%` }}
          />
        </div>

        <div className="card-container">
          <WordCard content={currentContent} size="large" />
        </div>

        <div className="learn-controls">
          <button
            className="btn btn-secondary"
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            ⬅️ Poprzednie
          </button>
          <button className="btn btn-primary btn-large" onClick={handleNext}>
            {currentIndex === set.content.length - 1 ? 'Zakończ ✅' : 'Następne ➡️'}
          </button>
        </div>
      </div>
    );
  }

  // Overview view (default)
  return (
    <div className="custom-set-learn fade-in">
      <div className="learn-header">
        <button className="btn btn-secondary btn-small" onClick={() => navigate(`/custom-sets/${id}`)}>
          ← Wróć
        </button>
        <h2>{set.icon} {set.name}</h2>
      </div>

      <div className="lesson-stats">
        <span className="stat-item">📝 {wordCount} słów</span>
        <span className="stat-item">💬 {sentenceCount} zdań</span>
      </div>

      <div className="learning-methods">
        <h3>Metody nauki</h3>
        <div className="methods-grid">
          <button className="method-tile" onClick={startWordByWord}>
            <span className="method-icon">📖</span>
            <span className="method-name">Słowo po słowie</span>
            <span className="method-desc">Przeglądaj słówka jedno po drugim</span>
          </button>
          <button className="method-tile" onClick={startSelfEval}>
            <span className="method-icon">🧠</span>
            <span className="method-name">Samosprawdzian</span>
            <span className="method-desc">Oceń swoją znajomość słówek</span>
          </button>
        </div>
      </div>

      <div className="lesson-content-list">
        <h3>Zawartość zestawu</h3>
        <div className="content-list">
          {[...set.content].sort((a, b) => {
            if (a.type !== b.type) return a.type === 'word' ? -1 : 1;
            return a.english.localeCompare(b.english);
          }).map((item, idx) => (
            <div key={idx} className={`content-item ${item.type}`}>
              <span className="content-english">{item.english}</span>
              <span className="content-polish">{item.polish}</span>
              <button
                className="speak-btn-small"
                onClick={() => speak(item.english)}
                title="Posłuchaj"
              >
                🔊
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomSetLearn;

